// Free coin rewards (shared by the browser and /api/rewards).
// Coins are credited ONLY on the server into users/{uid}.rewardCoins (clients cannot write that
// field - see lib/d1-rules.ts). Nothing here costs the user money.
import { ACHIEVEMENTS, type AchievementDef } from '@/lib/achievements'

/** AdMob TEST MODE. true = only Google's test ad units are used (live units are ignored). PRODUCTION = false (live ads). */
export const ADMOB_FORCE_TEST = false

/** Every achievement pays coins: xp / 25 (rounded up), so 25xp = 1 coin ... 500xp = 20 coins. */
export const ACHIEVEMENT_XP_PER_COIN = 25
/** Streak achievements pay extra ("streak poori karne pe"). Overrides the xp formula. */
export const STREAK_COIN_OVERRIDES: Record<string, number> = { streak_7: 10, streak_30: 50 }
export const achievementCoins = (a: AchievementDef): number =>
  STREAK_COIN_OVERRIDES[a.id] ?? Math.max(1, Math.ceil(a.xp / ACHIEVEMENT_XP_PER_COIN))
export const ACHIEVEMENT_COINS_MAP: Record<string, number> = Object.fromEntries(ACHIEVEMENTS.map((a) => [a.id, achievementCoins(a)]))

/** Max free coins from achievements per UTC day. */
export const ACHIEVEMENT_DAILY_COIN_CAP = 150

/** Green Tick milestone: BOTH must be reached -> permanent free Green Tick. */
export const TICK_MILESTONE_ALLIES = 1_000_000 // 1 million Allies
export const TICK_MILESTONE_ALLIES_LABEL = '1 million'
export const TICK_MILESTONE_VIEWS = 3_000_000 // 30 lakh total reel views

/** Boost is only for established creators (keeps Boost free of spam): BOTH must be reached. */
export const BOOST_MIN_ALLIES = 5_000 // 5k Allies (followers)
export const BOOST_MIN_VIEWS = 50_000 // 50k total reel views
export const boostEligible = (allies: number, views: number) => allies >= BOOST_MIN_ALLIES && views >= BOOST_MIN_VIEWS

/** Rewarded ads = GIFT BOXES ("Watch ad, open a box"). The wallet shows a row of boxes. Each box is opened by watching one ad
 *  and pays a FIXED amount (no random): box 1 = 50 coins, box 2 = 100 coins (the treasure chest), and so on.
 *  The row resets GIFT_ROW_RESET_MS after the first box of the row was opened, so a new row is ready every hour.
 *  AD_DAILY_CAP boxes per day in total (stops coin farming): the rest of the day the boxes stay empty. */
export const GIFT_BOX_REWARDS = [50, 100, 20, 30, 100] as const // coins in box 1..5 of a row (edit freely, keep the length = GIFT_ROW_SIZE)
export const GIFT_ROW_SIZE = GIFT_BOX_REWARDS.length
export const GIFT_ROW_RESET_MS = 60 * 60 * 1000 // a fresh row of boxes every hour
export const giftBoxReward = (index: number): number => GIFT_BOX_REWARDS[Math.max(0, Math.min(GIFT_ROW_SIZE - 1, index))]
// Kept for older code: smallest / biggest box.
export const AD_REWARD_OPTIONS = GIFT_BOX_REWARDS
export const AD_REWARD_MIN = Math.min(...GIFT_BOX_REWARDS)
export const AD_REWARD_MAX = Math.max(...GIFT_BOX_REWARDS)
export const AD_DAILY_CAP = 10 // max gift boxes (= rewarded ads) per UTC day: 10 boxes = at most 2 full rows = 600 coins/day
export const AD_COOLDOWN_MS = 2 * 60 * 1000 // minimum gap between two boxes
// A claim sooner than this after ad_start is refused. Was 12s: many rewarded ads (and Google's test ads) pay out after ~5-10s, so the
// reward event arrived BEFORE 12s and the server answered 'too-fast' -> NO coins. 4s still blocks scripted instant claims
// (the real proof is the AdMob reward event + SSV in live mode).
export const AD_MIN_WATCH_MS = 4 * 1000
export const AD_TICKET_TTL_MS = 10 * 60 * 1000 // ad_start ticket must be claimed within this time

/** App Open ad reward: every App Open ad that is really shown pays a small bonus (server-decided, capped). */
// App Open ads give NO coins (0). Policy-safe: AdMob only allows rewards for rewarded formats.
export const APP_OPEN_REWARD_COINS = 0
export const APP_OPEN_DAILY_CAP = 10 // max App Open ads per UTC day
export const APP_OPEN_COOLDOWN_MS = 30 * 60 * 1000 // minimum gap between two App Open ads (the ad is only shown when this has passed)
export const APP_OPEN_MIN_MS = 2 * 1000 // a claim sooner than this after app_open_start is refused
export const APP_OPEN_TICKET_TTL_MS = 10 * 60 * 1000

// ---- "Watch an ad to DOUBLE your win" (Chess + Ludo result card, winner of a coin room only) ----
// Bonus = the prize that was just won (so the total is doubled), but never more than DOUBLE_MAX_BONUS coins per game.
// Raise / lower these to change the economy. One double per game, DOUBLE_DAILY_CAP per player per UTC day.
export const DOUBLE_MAX_BONUS = 1000
export const DOUBLE_DAILY_CAP = 5
export const DOUBLE_CLAIM_WINDOW_MS = 15 * 60 * 1000 // the offer expires 15 minutes after the game ended

// ---- Daily check-in streak (Comms card): coins for day 1..7 of a streak, then the cycle repeats ----
// Miss a (UTC) day and the streak restarts at day 1. Day 7 is the big bonus. Raise / lower these to change the economy.
export const DAILY_STREAK_COINS = [2, 2, 3, 3, 4, 5, 15]

/** Daily missions: verified on the server from real finished ONLINE games (dailyActivity/{uid}_{day}), paid once per UTC day. */
export type MissionDef = { id: string; label: string; stat: 'played' | 'wins'; target: number; coins: number }
export const DAILY_MISSIONS: MissionDef[] = [
  { id: 'play1', label: 'Finish 1 online game', stat: 'played', target: 1, coins: 2 },
  { id: 'win1', label: 'Win 1 online game', stat: 'wins', target: 1, coins: 4 },
  { id: 'play3', label: 'Finish 3 online games', stat: 'played', target: 3, coins: 5 },
]
/** Weekly tournament: top 3 by online wins (min. WEEKLY_MIN_WINS) of a finished UTC week can claim these coins. */
export const WEEKLY_PRIZES = [30, 20, 10] as const
export const WEEKLY_MIN_WINS = 3
